import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './BaseModal.css';

/**
 * BaseModal - Reusable modal foundation component
 * 
 * Features:
 * - Focus trap with keyboard navigation
 * - ESC key to close
 * - Backdrop click detection
 * - Body scroll lock
 * - Focus restoration on close
 * - Token-based styling
 * - Sticky footer support
 */
export default function BaseModal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = '920px',
  className = '',
  restoreFocusEl,
  showHeader = true
}) {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store focus on mount
  useEffect(() => {
    if (isOpen && !previousActiveElement.current) {
      previousActiveElement.current = document.activeElement;
    }
  }, [isOpen]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus restoration on close
  useEffect(() => {
    if (!isOpen && previousActiveElement.current) {
      // Restore focus to original element
      if (restoreFocusEl) {
        restoreFocusEl?.focus?.();
      } else {
        previousActiveElement.current?.focus?.();
      }
      previousActiveElement.current = null;
    }
  }, [isOpen, restoreFocusEl]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable?.focus();
          e.preventDefault();
        }
      }
    };

    modal.addEventListener('keydown', handleTab);
    
    // Focus first element
    setTimeout(() => firstFocusable?.focus(), 50);

    return () => modal.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Backdrop click handler
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const portalElement = document.getElementById('root') || document.body;

  return ReactDOM.createPortal(
    <div className="base-modal-backdrop" onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={`base-modal ${className}`}
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {showHeader && title && (
          <div className="base-modal-header">
            <h2 id="modal-title" className="base-modal-title">{title}</h2>
          </div>
        )}
        <div className="base-modal-body">
          {children}
        </div>
        {footer && (
          <div className="base-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    portalElement
  );
}
